import { supabase } from "@/integrations/supabase/client";

/** Settings are now read from org config in the database.
 *  localStorage is no longer used for API settings.
 *  Edge functions use EVOLUTION_API_URL env var directly. */
export function getApiSettings() {
  return { timezone: "America/Sao_Paulo" };
}

export function saveApiSettings(_settings: Record<string, unknown>) {
  // No-op — settings are managed via org configuration in the database
}

// Dashboard
export const dashboardApi = {
  getMetrics: async () => {
    const tz = getApiSettings().timezone;
    const { data, error } = await supabase.rpc("get_dashboard_metrics", { _tz: tz } as any);
    if (error) throw new Error(error.message);
    return data as { active_groups: number; total_members: number; messages_today: number; summaries_generated: number };
  },
  getMessagesPerDay: async () => {
    const tz = getApiSettings().timezone;
    const { data, error } = await supabase.rpc("get_messages_per_day", { _tz: tz } as any);
    if (error) throw new Error(error.message);
    return (data ?? []) as { date: string; count: number }[];
  },
  getMessagesPerGroup: async () => {
    const { data, error } = await supabase.rpc("get_messages_per_group");
    if (error) throw new Error(error.message);
    return (data ?? []) as { group_name: string; count: number }[];
  },
};

// Groups
export const groupsApi = {
  list: async () => {
    const { data, error } = await supabase.from("syra_groups").select("*").order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  },
  update: async (id: string, updates: Record<string, unknown>) => {
    const { data, error } = await supabase.from("syra_groups").update(updates as any).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data;
  },
  toggleActive: async (id: string, active: boolean) => {
    const { data, error } = await supabase.from("syra_groups").update({ active }).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data;
  },
  create: async (group: { name: string; group_jid: string; org_id: string; config?: Record<string, unknown>; instance_name?: string }) => {
    // Onda C #7 — best-effort canonicalize JID antes de inserir.
    // Se a edge não responder ou retornar erro, segue com o JID original
    // (índice único `(org_id, group_jid)` ainda protege contra duplicata exata).
    let canonicalJid = group.group_jid;
    let legacyJid: string | null = null;
    if (group.group_jid && group.org_id) {
      try {
        const { data: canon } = await supabase.functions.invoke(
          "groups-canonicalize-jid",
          {
            body: {
              jid: group.group_jid,
              org_id: group.org_id,
              instance_name: group.instance_name ?? null,
            },
          },
        );
        if (canon?.canonical_jid && canon.canonical_jid !== group.group_jid) {
          canonicalJid = canon.canonical_jid;
          legacyJid = group.group_jid;
        }
      } catch (e) {
        console.warn("[groups.create] canonicalize failed, using raw jid:", e);
      }
    }
    const payload = {
      ...group,
      group_jid: canonicalJid,
      legacy_jid: legacyJid,
      instance_status: canonicalJid ? "connected" : "disconnected",
    };
    const { data, error } = await supabase.from("syra_groups").insert([payload as any]).select().single();
    if (error) throw new Error(error.message);
    return data;
  },
  delete: async (id: string) => {
    const { error } = await supabase.from("syra_groups").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },
};

// Members
export const membersApi = {
  list: async (filters?: { classification?: string; search?: string; page?: number; limit?: number }) => {
    const limit = filters?.limit ?? 20;
    const page = filters?.page ?? 1;
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let query = supabase.from("syra_members").select("*", { count: "exact" }).order("engagement_score", { ascending: false }).range(from, to);
    if (filters?.classification) query = query.eq("classification", filters.classification as "contribuidor_ativo" | "participante" | "observador" | "inativo");
    if (filters?.search) query = query.ilike("name", `%${filters.search}%`);
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  },
};

// Sources
export const sourcesApi = {
  list: async (filters?: { group_id?: string; page?: number; limit?: number }) => {
    const limit = filters?.limit ?? 20;
    const page = filters?.page ?? 1;
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    let query = supabase.from("syra_sources").select("*", { count: "exact" }).order("created_at", { ascending: false }).range(from, to);
    if (filters?.group_id) query = query.eq("group_id", filters.group_id);
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  },
  create: async (source: { name: string; type: string; url: string; group_id: string; group_name: string; categories: string[]; active: boolean }) => {
    // Get user's org_id
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Usuário não autenticado");
    const { data: membership } = await supabase.from("org_members").select("org_id").eq("user_id", user.id).limit(1).single();
    if (!membership) throw new Error("Usuário sem organização");
    const { data, error } = await supabase.from("syra_sources").insert([{ ...source, org_id: membership.org_id } as any]).select().single();
    if (error) throw new Error(error.message);
    return data;
  },
  update: async (id: string, updates: Record<string, unknown>) => {
    const { data, error } = await supabase.from("syra_sources").update(updates as any).eq("id", id).select().single();
    if (error) throw new Error(error.message);
    return data;
  },
  delete: async (id: string) => {
    const { error } = await supabase.from("syra_sources").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },
  test: async (id: string) => {
    try {
      // Get the source URL
      const { data: source, error } = await supabase.from("syra_sources").select("url").eq("id", id).single();
      if (error || !source?.url) return { success: false, message: "Fonte não encontrada" };

      // Test via backend to avoid CORS
      const { data, error: fnError } = await supabase.functions.invoke("test-connection", {
        body: { url: source.url },
      });
      if (fnError) return { success: false, message: "Falha ao testar conexão" };

      // Update last_fetched on success
      if (data?.ok) {
        await supabase.from("syra_sources").update({ last_fetched: new Date().toISOString() } as any).eq("id", id);
      }

      return { success: data?.ok ?? false, message: data?.message || "Sem resposta" };
    } catch {
      return { success: false, message: "Erro ao testar fonte" };
    }
  },
};

// Users (profiles + roles)
export const usersApi = {
  list: async () => {
    const { data: result, error } = await supabase.functions.invoke("list-users");
    if (error) throw new Error(error.message);
    if (result?.error) throw new Error(result.error);
    return (result ?? []) as {
      id: string;
      name: string;
      email: string;
      role: string;
      active: boolean;
      created_at: string;
      last_login?: string;
    }[];
  },
  create: async (data: { name: string; email: string; password: string; role: string }) => {
    const { data: result, error } = await supabase.functions.invoke("create-user", {
      body: { name: data.name, email: data.email, password: data.password, role: data.role },
    });
    if (error) throw new Error(error.message);
    if (result?.error) throw new Error(result.error);
    return result as { id: string; name: string; email: string; role: "admin" | "viewer"; active: boolean };
  },
  toggleActive: async (id: string, active: boolean) => {
    const { error } = await supabase.from("profiles").update({ active }).eq("id", id);
    if (error) throw new Error(error.message);
  },
  update: async (data: { user_id: string; name?: string; role?: string; confirm_email?: boolean }) => {
    const { data: result, error } = await supabase.functions.invoke("update-user", {
      body: data,
    });
    if (error) throw new Error(error.message);
    if (result?.error) throw new Error(result.error);
    return result;
  },
  delete: async (userId: string) => {
    const { data: result, error } = await supabase.functions.invoke("update-user", {
      body: { user_id: userId, delete_user: true },
    });
    if (error) throw new Error(error.message);
    if (result?.error) throw new Error(result.error);
    return result;
  },
  confirmEmail: async (userId: string) => {
    const { data: result, error } = await supabase.functions.invoke("update-user", {
      body: { user_id: userId, confirm_email: true },
    });
    if (error) throw new Error(error.message);
    if (result?.error) throw new Error(result.error);
    return result;
  },
};

// Prompt Library
export interface PromptLibraryItem {
  id: string;
  org_id: string;
  name: string;
  description: string;
  template: string;
  variables: string[];
  category: string;
  typology: string | null;
  tags: string[];
  preferred_model: string | null;
  temperature_default: number | null;
  version: number;
  active: boolean;
  is_system_default: boolean;
  created_at: string;
  updated_at: string;
}

export const promptLibraryApi = {
  list: async (): Promise<PromptLibraryItem[]> => {
    const { data, error } = await supabase
      .from("prompt_library").select("*").eq("active", true)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as PromptLibraryItem[];
  },
  listArchived: async (): Promise<PromptLibraryItem[]> => {
    const { data, error } = await supabase
      .from("prompt_library").select("*").eq("active", false)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as unknown as PromptLibraryItem[];
  },
  create: async (input: Partial<PromptLibraryItem> & { name: string; template: string }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Usuário não autenticado");
    const { data: membership } = await supabase
      .from("org_members").select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
    if (!membership) throw new Error("Organização não encontrada");
    const payload = {
      org_id: membership.org_id,
      name: input.name,
      description: input.description ?? "",
      template: input.template,
      variables: (input.variables ?? []) as any,
      category: input.category ?? "general",
      typology: input.typology ?? null,
      tags: (input.tags ?? []) as any,
      preferred_model: input.preferred_model ?? null,
      temperature_default: input.temperature_default ?? 0.7,
      created_by: user.id,
    };
    const { data, error } = await supabase.from("prompt_library").insert([payload as any]).select().maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },
  update: async (id: string, updates: Partial<PromptLibraryItem>) => {
    const { data, error } = await supabase
      .from("prompt_library").update(updates as any).eq("id", id).select().maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },
  archive: async (id: string) => {
    const { error } = await supabase.from("prompt_library").update({ active: false } as any).eq("id", id);
    if (error) throw new Error(error.message);
  },
  unarchive: async (id: string) => {
    const { error } = await supabase.from("prompt_library").update({ active: true } as any).eq("id", id);
    if (error) throw new Error(error.message);
  },
  remove: async (id: string) => {
    const { error } = await supabase.from("prompt_library").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },
  test: async (input: {
    template: string;
    variables?: Record<string, string>;
    user_message: string;
    model?: string;
    temperature?: number;
    mode?: "quick" | "simulate";
    group_id?: string | null;
  }) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Usuário não autenticado");
    const { data: membership } = await supabase
      .from("org_members").select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
    if (!membership) throw new Error("Organização não encontrada");
    const { data, error } = await supabase.functions.invoke("prompt-library-test", {
      body: { ...input, org_id: membership.org_id },
    });
    if (error) throw new Error(error.message);
    return data as {
      ok: boolean;
      response: string;
      model_used: string;
      provider_used: string | null;
      tokens_in: number | null;
      tokens_out: number | null;
      latency_ms: number;
      rendered_system_prompt: string;
      simulation_context: Record<string, unknown>;
    };
  },
};

// Compat para componentes legados que ainda chamam promptsApi.list()
export const promptsApi = {
  list: async () => {
    const items = await promptLibraryApi.list();
    return items.map((p) => ({
      id: p.id, name: p.name, template: p.template, variables: p.variables,
      version: p.version, active: p.active, updated_at: p.updated_at,
    }));
  },
};

// Logs
export const logsApi = {
  list: async (filters?: { group_id?: string; type?: string; limit?: number }) => {
    let query = supabase.from("syra_logs").select("*").order("created_at", { ascending: false }).limit(filters?.limit ?? 50);
    if (filters?.group_id) query = query.eq("group_id", filters.group_id);
    if (filters?.type) query = query.eq("type", filters.type as "info" | "warning" | "error");
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return data as { id: string; group_id: string; group_name: string; type: "info" | "warning" | "error"; message: string; details: string | null; created_at: string }[];
  },
};

// AI Agents
export const agentsApi = {
  list: async () => {
    const { data, error } = await supabase.from("syra_ai_agents_safe").select("*").order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  },
  create: async <T extends object>(agent: T) => {
    const { data, error } = await supabase.functions.invoke("agent-upsert", { body: agent });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data?.data ?? data;
  },
  update: async (id: string, updates: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("agent-upsert", { body: { id, ...updates } });
    if (error) throw new Error(error.message);
    if (data?.error) throw new Error(data.error);
    return data?.data ?? data;
  },
  delete: async (id: string) => {
    const { error } = await supabase.from("syra_ai_agents").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },
  history: async (agentId: string) => {
    const { data, error } = await supabase.from("syra_ai_agent_history").select("*").eq("agent_id", agentId).order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data;
  },
  generateSummary: async (groupId: string, period: "daily" | "weekly" | "monthly") => {
    const { data, error } = await supabase.functions.invoke("generate-summary", {
      body: { group_id: groupId, period },
    });
    if (error) throw new Error(error.message);
    return data;
  },
};

// Connectivity test via backend
export const testConnection = async (url: string): Promise<{ ok: boolean; message: string }> => {
  try {
    const { data, error } = await supabase.functions.invoke("test-connection", {
      body: { url },
    });
    if (error) return { ok: false, message: "Falha ao testar conexão" };
    return data as { ok: boolean; message: string };
  } catch {
    return { ok: false, message: "Sem conexão" };
  }
};
